import Modal from '@/components/ui/Modal';
import Button from '@/components/ui/Button';
import type { BrandKit } from './types';

interface ApplyKitDialogProps {
  kit: BrandKit | null;
  busy: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

/**
 * Confirm-before-apply with a client-side preview of what changes: the core
 * swatches, fonts, and a sample header strip painted straight from the kit's
 * identity (no server call until Confirm). Apply snapshots the previous
 * settings, so the dialog can honestly promise a one-click revert.
 */
export default function ApplyKitDialog({ kit, busy, onConfirm, onClose }: ApplyKitDialogProps) {
  if (!kit) return null;
  const palette = kit.identity.palette;
  const primary = kit.identity.primaryColor ?? '#4a7c59';
  const primaryDark = palette?.primary?.['700'] ?? primary;
  const accent = kit.identity.accentColor ?? palette?.accent?.base ?? primary;
  const cream = palette?.warm?.cream ?? '#f7f5f0';
  const fonts = kit.identity.fonts;

  const swatches: Array<{ label: string; hex: string }> = [
    { label: 'Primary', hex: primary },
    { label: 'Primary dark', hex: primaryDark },
    { label: 'Accent', hex: accent },
    { label: 'Cream', hex: cream },
  ];

  return (
    <Modal isOpen onClose={onClose} title={`Apply “${kit.name}” to your storefront?`}>
      <div className="space-y-4">
        {/* Sample header strip in the kit's colors */}
        <div className="overflow-hidden rounded-lg border border-line">
          <div className="flex items-center gap-3 px-4 py-3" style={{ backgroundColor: primaryDark }}>
            <span className="text-sm font-semibold text-white">
              {kit.brief.businessName ?? kit.name}
            </span>
            <span className="ml-auto h-2 w-16 rounded-full" style={{ backgroundColor: accent }} />
          </div>
          <div className="px-4 py-3 text-sm text-ink" style={{ backgroundColor: cream }}>
            Buttons, links, and highlights repaint in these colors immediately.
          </div>
        </div>

        <div className="grid grid-cols-4 gap-2">
          {swatches.map((s) => (
            <div key={s.label} className="text-center">
              <div
                className="mx-auto h-10 w-full rounded-md border border-line"
                style={{ backgroundColor: s.hex }}
              />
              <div className="mt-1 text-[11px] text-ink-muted">{s.label}</div>
              <div className="text-[10px] font-mono text-ink-soft">{s.hex}</div>
            </div>
          ))}
        </div>

        <ul className="list-disc space-y-1 pl-5 text-sm text-ink-soft">
          <li>Site colors switch to this palette (full scope, dark mode included).</li>
          {fonts && (
            <li>
              Fonts become <span className="font-medium text-ink">{fonts.heading}</span> (headings)
              and <span className="font-medium text-ink">{fonts.body}</span> (body).
            </li>
          )}
          <li>The header logo and favicon are replaced with this kit&rsquo;s versions.</li>
          <li>Your current look is snapshotted — you can revert with one click.</li>
        </ul>

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={onConfirm} isLoading={busy}>
            Apply to storefront
          </Button>
        </div>
      </div>
    </Modal>
  );
}
